import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import NavigatorOptionConfig from "./NavigatorOptionConfig";
import { getEnumValuesArray } from "../../../utils/enums";
import { LaneOverlapping, WallDistance } from "../../../enums/configuration/navigator-settings";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import {
  selectNavLocalPlannerData,
  selectNavTasksData,
  selectParamList,
  selectParamListRestState,
} from "../../../store/robot-configurations/robot-configurations-selectors";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import OptionSwitchInput from "../../Inputs/OptionSwitchInput";
import CustomSplitColumnCard from "../../misc/CustomSplitColumnCard";
import { TeachAndRepeatResolveType, getTeachAndRepeatResolveTypeList } from "../../../enums/robot/rooms/teach-and-repeat-resolve-type";
import { selectRobotRswSoftwareVersion } from "../../../store/robot-configurations/robot-configurations-selectors";
import { isVersionAGreaterThanB } from "../../../utils/version-util";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";

export const allowedDeviationSliderMin = 0.25;
export const allowedDeviationSliderMax = 1.75;
export const allowedDeviationSliderStep = 0.25;

const NavigatorSettingsConfig: React.FC<{ webSoftwareVersion?: string | null }> = ({ webSoftwareVersion }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const {
    getNavLocalPlanner,
    getParamList,
    getNavTasks,
    patchNavTasks,
    patchNavLocalPlanner,
    patchParamList,
    patchNavLocalPlannerRestSet,
    patchNavTasksRestSet,
    patchParamListRestSet
  } = useSharedActions();

  const localPlannerData = useSharedSelector((state) => selectNavLocalPlannerData(state));
  const tasksData = useSharedSelector((state) => selectNavTasksData(state));
  const paramList = useSharedSelector((state) => selectParamList(state));
  const paramListRest = useSharedSelector((state) => selectParamListRestState(state));
  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const [snackbarGuard, setSnackbarGuard] = useState(false);

  useEffect(() => {
    if (localPlannerData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getNavLocalPlanner());
    }
  }, [dispatch, getNavLocalPlanner, localPlannerData.rest.getStatus]);

  useEffect(() => {
    if (paramListRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getParamList());
    }
  }, [dispatch, getParamList, paramListRest.getStatus]);

  useEffect(() => {
    if (tasksData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getNavTasks());
    }
  }, [dispatch, getNavTasks, tasksData.rest.getStatus]);

  const configurationSnackbarCloseHandler = () => {
    dispatch(patchNavLocalPlannerRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
    dispatch(patchNavTasksRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
    dispatch(patchParamListRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  const laneOverlappingChangeHandler = (value: LaneOverlapping) => {
    setSnackbarGuard(true);
    dispatch(patchParamList({ openSpaceLaneMode: value }));
  };

  const wallDistanceChangeHandler = (value: WallDistance) => {
    setSnackbarGuard(true);
    dispatch(patchParamList({ openSpaceWallDistanceMode: value }));
  };

  const pathDeviationChangeHandler = (value: number) => {
    setSnackbarGuard(true);
    dispatch(patchNavLocalPlanner({ virtualCorridorParams: { allowedDeviation: value } }));
  };

  const resolveConfigChangeHandler = (value: TeachAndRepeatResolveType) =>{
    setSnackbarGuard(true);
    dispatch(patchNavLocalPlanner({virtualCorridorParams: {resolveConfiguration: value}}))
  }

  const spotCleaningRadiusChangeHandler = (value: number) => {
    setSnackbarGuard(true);
    dispatch(patchNavTasks({ SpotCleaning: { spotCleanRadius: value } }));
  };

  const rswSoftwareVersion = useSharedSelector((state) => selectRobotRswSoftwareVersion(state));

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("navigator-settings-title")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <Typography>{t("structured-cleaning-settings-title")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack spacing={2}>
                <OptionSwitchInput
                  label={t("navigator-settings-lane-overlapping")}
                  options={getEnumValuesArray(LaneOverlapping).map((k) => ({
                    value: k,
                    label: t(`lane-overlapping-option-${k}`),
                  }))}
                  loading={paramList == null}
                  value={paramList?.openSpaceLaneMode}
                  onChange={(v: string) => laneOverlappingChangeHandler(v as LaneOverlapping)}
                  readOnly={!isWebapp && (!webSoftwareVersion || isVersionAGreaterThanB("2026.1.0", webSoftwareVersion))}
                />
                <OptionSwitchInput
                  label={t("navigator-settings-wall-distance")}
                  options={getEnumValuesArray(WallDistance).map((k) => ({
                    value: k,
                    label: t(`wall-distance-option-${k}`),
                  }))}
                  loading={paramList == null}
                  value={paramList?.openSpaceWallDistanceMode}
                  onChange={(v: string) => wallDistanceChangeHandler(v as WallDistance)}
                  readOnly={!isWebapp && (!webSoftwareVersion || isVersionAGreaterThanB("2026.1.0", webSoftwareVersion))}
                />
              </Stack>
            ),
          },
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <Typography>{t("teach-and-repeat-settings-title")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack spacing={2}>
                <NavigatorOptionConfig
                  label="path-deviation"
                  loading={localPlannerData.data == null}
                  value={localPlannerData.data?.virtualCorridorParams.allowedDeviation}
                  onChange={pathDeviationChangeHandler}
                  sliderOptions={{
                    step: allowedDeviationSliderStep,
                    min: allowedDeviationSliderMin,
                    max: allowedDeviationSliderMax,
                  }}
                />
                {(isWebapp || (rswSoftwareVersion && !isVersionAGreaterThanB("2026.1.0", rswSoftwareVersion))) && (
                  <OptionSwitchInput
                    label={t("resolveConfiguration")}
                    value={localPlannerData.data?.virtualCorridorParams.resolveConfiguration}
                    options={getTeachAndRepeatResolveTypeList.map((rt) => ({ value: rt, label: t(rt), helpText: t(`${rt}-tooltip`) }))}
                    onChange={resolveConfigChangeHandler}
                  />
                )}
              </Stack>
            ),
          },
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <Typography>{t("spot-cleaning-settings-title")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <NavigatorOptionConfig
                label="spot-cleaning-radius"
                loading={tasksData.data == null}
                value={tasksData.data?.SpotCleaning.spotCleanRadius}
                onChange={spotCleaningRadiusChangeHandler}
                sliderOptions={{ step: 1, min: 0, max: 100 }}
              />
            ),
          },
        ]}
      />

      <CustomSnackbar
        title={t("error-saving-navigator-settings")}
        open={
          snackbarGuard &&
          (tasksData.rest.patchStatus === HttpFetchStatus.Failed ||
            localPlannerData.rest.patchStatus === HttpFetchStatus.Failed ||
            paramListRest.patchStatus === HttpFetchStatus.Failed)
        }
        onClose={configurationSnackbarCloseHandler}
        textMessages={getSaveErrorMessages(tasksData.rest.patchError)
          .concat(getSaveErrorMessages(localPlannerData.rest.patchError))
          .concat(getSaveErrorMessages(paramListRest.patchError))}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-navigator-settings")}
        severity="success"
        open={
          snackbarGuard &&
          (tasksData.rest.patchStatus === HttpFetchStatus.Success ||
            localPlannerData.rest.patchStatus === HttpFetchStatus.Success ||
            paramListRest.patchStatus === HttpFetchStatus.Success)
        }
        onClose={configurationSnackbarCloseHandler}
      />
    </React.Fragment>
  );
};

export default NavigatorSettingsConfig;
